import { useState, useEffect, useRef, useCallback } from 'react';
import { Editor, Frame, Element, useEditor } from '@craftjs/core';
import {
  ArrowLeft,
  Settings,
  Eye,
  Save,
  Globe,
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import {
  TextBlock,
  HeadingBlock,
  ImageBlock,
  VideoBlock,
  QuoteBlock,
  CTABlock,
  DividerBlock,
  SpacerBlock,
  CodeBlock,
  EmbedBlock,
  ColumnsBlock,
  ColumnContainer,
  BlockToolbar,
  BlockSettings,
} from './blocks';
import { PostSettingsSidebar } from './PostSettingsSidebar';
import { PostPreview } from './PostPreview';
import type { BlogPost, BlogCategory, BlogTag } from '../../../types';

interface PostEditorViewProps {
  post: BlogPost | null;
  categories: BlogCategory[];
  tags: BlogTag[];
  loading: boolean;
  onSave: (data: SavePostData) => Promise<void>;
  onAutoSave: (content: string) => Promise<void>;
  onPublish: () => Promise<void>;
  onBack: () => void;
  onCreateCategory?: (data: { name: string; color?: string }) => Promise<{ success: boolean; error?: string }>;
  onCreateTag?: (name: string) => Promise<{ success: boolean; error?: string }>;
  accessToken?: string | null;
}

export interface SavePostData {
  title: string;
  slug: string;
  excerpt: string;
  coverImageUrl: string;
  seoTitle: string;
  seoDescription: string;
  categoryIds: string[];
  tagIds: string[];
  content?: string;
}

// Craft.js resolver — maps component names to components
const resolver = {
  TextBlock,
  HeadingBlock,
  ImageBlock,
  VideoBlock,
  QuoteBlock,
  CTABlock,
  DividerBlock,
  SpacerBlock,
  CodeBlock,
  EmbedBlock,
  ColumnsBlock,
  ColumnContainer,
};

export function PostEditorView({
  post,
  categories,
  tags,
  loading,
  onSave,
  onAutoSave,
  onPublish,
  onBack,
  onCreateCategory,
  onCreateTag,
  accessToken,
}: PostEditorViewProps) {
  const [showPreview, setShowPreview] = useState(false);
  const [showSettings, setShowSettings] = useState(true);
  const [title, setTitle] = useState(post?.title ?? '');
  const [slug, setSlug] = useState(post?.slug ?? '');
  const [excerpt, setExcerpt] = useState(post?.excerpt ?? '');
  const [coverImageUrl, setCoverImageUrl] = useState(post?.coverImageUrl ?? '');
  const [seoTitle, setSeoTitle] = useState(post?.seoTitle ?? '');
  const [seoDescription, setSeoDescription] = useState(post?.seoDescription ?? '');
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>(
    post?.categories?.map((c) => c.id) ?? []
  );
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>(
    post?.tags?.map((t) => t.id) ?? []
  );
  const [saving, setSaving] = useState(false);

  // Serialize content — serializeNowRef is populated by AutoSaver with a
  // function that calls query.serialize() on demand (inside <Editor> context).
  const serializedContentRef = useRef<string>('');
  const serializeNowRef = useRef<(() => string) | null>(null);

  /** Get the latest serialized content — fresh if possible, fallback to last auto-save */
  const getContent = useCallback(() => {
    if (serializeNowRef.current) {
      try {
        const json = serializeNowRef.current();
        serializedContentRef.current = json;
        return json;
      } catch { /* fall through */ }
    }
    return serializedContentRef.current;
  }, []);

  const buildSaveData = useCallback((): SavePostData => ({
    title,
    slug,
    excerpt,
    coverImageUrl,
    seoTitle: seoTitle || title.slice(0, 70),
    seoDescription,
    categoryIds: selectedCategoryIds,
    tagIds: selectedTagIds,
    content: getContent(),
  }), [
    title,
    slug,
    excerpt,
    coverImageUrl,
    seoTitle,
    seoDescription,
    selectedCategoryIds,
    selectedTagIds,
    getContent,
  ]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      await onSave(buildSaveData());
    } finally {
      setSaving(false);
    }
  }, [buildSaveData, onSave]);

  // Save all data before navigating back
  const handleBack = useCallback(async () => {
    try {
      await onSave(buildSaveData());
    } catch {
      // Continue navigating even if save fails
    }
    onBack();
  }, [buildSaveData, onSave, onBack]);

  // Auto-generate slug from title (only when slug is empty / untouched)
  useEffect(() => {
    if (!post?.slug && title && !slug) {
      const generated = title
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      setSlug(generated);
    }
  }, [title, post?.slug, slug]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-64px)]">
        <p className="text-muted-foreground text-sm">Carregando editor...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-64px)]">
      {/* Top Bar */}
      <div className="flex items-center justify-between px-4 py-3 border-b bg-background shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <Button variant="ghost" size="icon" onClick={handleBack} className="shrink-0">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Título do post..."
            className="text-lg font-semibold border-none shadow-none focus-visible:ring-0 max-w-md"
          />
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button variant="outline" size="sm" onClick={() => setShowSettings(!showSettings)}>
            <Settings className="h-4 w-4 mr-2" />
            {showSettings ? 'Ocultar' : 'Configurações'}
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowPreview(true)}>
            <Eye className="h-4 w-4 mr-2" />
            Visualizar
          </Button>
          <Button variant="outline" size="sm" onClick={handleSave} disabled={saving}>
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Salvando...' : 'Salvar'}
          </Button>
          <Button size="sm" onClick={async () => {
            await onSave(buildSaveData());
            await onPublish();
          }}>
            <Globe className="h-4 w-4 mr-2" />
            Publicar
          </Button>
        </div>
      </div>

      {/* Editor Layout */}
      <Editor resolver={resolver}>
        <div className="flex flex-1 overflow-hidden">
          {/* Left: Block Toolbar */}
          <div className="w-56 border-r overflow-y-auto bg-muted/30 shrink-0">
            <BlockToolbar />
          </div>

          {/* Center: Canvas */}
          <div className="flex-1 overflow-y-auto p-8 bg-muted/10">
            <div className="max-w-4xl mx-auto min-h-[600px] bg-background rounded-lg shadow-sm border p-8 pb-40">
              {post?.content && post.content !== '{}' && post.content !== '' ? (
                <Frame data={post.content} />
              ) : (
                <Frame>
                  <Element
                    is="div"
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore — canvas is a Craft.js-specific prop
                    canvas
                    className="space-y-4 min-h-[400px]"
                  >
                    {/* Initial empty canvas — user drags blocks here */}
                  </Element>
                </Frame>
              )}
            </div>
          </div>

          {/* Right: Block Properties + Post Settings */}
          {showSettings && (
            <div className="w-80 border-l overflow-y-auto bg-background shrink-0">
              <div className="border-b">
                <BlockSettings />
              </div>
              <PostSettingsSidebar
                postTitle={title}
                slug={slug}
                onSlugChange={setSlug}
                excerpt={excerpt}
                onExcerptChange={setExcerpt}
                coverImageUrl={coverImageUrl}
                onCoverImageChange={setCoverImageUrl}
                seoTitle={seoTitle}
                onSeoTitleChange={setSeoTitle}
                seoDescription={seoDescription}
                onSeoDescriptionChange={setSeoDescription}
                categories={categories}
                selectedCategoryIds={selectedCategoryIds}
                onCategoryChange={setSelectedCategoryIds}
                onCreateCategory={onCreateCategory}
                tags={tags}
                selectedTagIds={selectedTagIds}
                onTagChange={setSelectedTagIds}
                onCreateTag={onCreateTag}
                accessToken={accessToken}
              />
            </div>
          )}
        </div>

        {/* Auto-save component — lives inside <Editor> context */}
        <AutoSaver
          onAutoSave={onAutoSave}
          onContentChange={(json) => {
            serializedContentRef.current = json;
          }}
          serializeNowRef={serializeNowRef}
        />
      </Editor>

      {/* Preview Overlay */}
      {showPreview && (
        <PostPreview
          title={title}
          coverImageUrl={coverImageUrl}
          onClose={() => setShowPreview(false)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// AutoSaver — must render inside <Editor> to access useEditor()
// ---------------------------------------------------------------------------

interface AutoSaverProps {
  onAutoSave: (content: string) => Promise<void>;
  onContentChange: (json: string) => void;
  /** Ref populated with a function to serialize on demand (for save/publish) */
  serializeNowRef: React.MutableRefObject<(() => string) | null>;
}

function AutoSaver({ onAutoSave, onContentChange, serializeNowRef }: AutoSaverProps) {
  const { query } = useEditor();
  const lastSavedRef = useRef('');
  const onAutoSaveRef = useRef(onAutoSave);
  const onContentChangeRef = useRef(onContentChange);

  // Keep refs up to date without adding to dependency array
  useEffect(() => {
    onAutoSaveRef.current = onAutoSave;
    onContentChangeRef.current = onContentChange;
  });

  // Expose on-demand serialize so Save/Publish always get the latest content
  useEffect(() => {
    serializeNowRef.current = () => query.serialize();
    return () => { serializeNowRef.current = null; };
  }, [query, serializeNowRef]);

  // Serialize immediately on mount so the content ref is populated as fallback
  useEffect(() => {
    try {
      const json = query.serialize();
      onContentChangeRef.current(json);
      lastSavedRef.current = json;
    } catch {
      // Ignore — editor may not be fully initialized yet
    }
  }, [query]);

  // Debounced auto-save: check every second, save 3s after last change
  useEffect(() => {
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;

    const interval = setInterval(() => {
      try {
        const json = query.serialize();
        onContentChangeRef.current(json);

        if (json !== lastSavedRef.current) {
          // Content changed — reset the 3s debounce timer
          if (debounceTimer) clearTimeout(debounceTimer);
          debounceTimer = setTimeout(() => {
            // Re-serialize to get the absolute latest
            try {
              const fresh = query.serialize();
              onContentChangeRef.current(fresh);
              if (fresh !== lastSavedRef.current) {
                lastSavedRef.current = fresh;
                onAutoSaveRef.current(fresh).catch(() => {});
              }
            } catch { /* ignore */ }
          }, 3_000);
        }
      } catch {
        // Ignore serialization errors during active editing
      }
    }, 1_000); // Poll every 1s for changes

    return () => {
      clearInterval(interval);
      if (debounceTimer) clearTimeout(debounceTimer);
    };
  }, [query]);

  return null;
}
